import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { Platform, InteractionManager } from 'react-native';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useTranslation } from './useTranslation';
import { trackEarnCoins, trackSpendCoins } from '@/src/analytics';
import { getApiUrl, getSupabaseAnonKey } from '@/lib/apiConfig';
import { isEpisodePurchased, markEpisodeAsPurchased } from '@/services/episodePurchaseStorage';
import { supabaseRequestDeduplicator } from '@/services/supabaseRequestDeduplicator';
import { userProfileCacheService } from '@/services/userProfileCacheService';
import { withSupabaseResult } from '@/utils/supabase/withSupabaseResult';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const gamLog = createScopedLogger('Gamification');

export interface GamificationEvent {
  id: string;
  event_type: string;
  title: string;
  description: string;
  coins_reward: number;
  is_active: boolean;
  metadata: any;
  translations?: {
    title: string;
    description: string;
    message: string;
  };
}

export interface UserAchievement {
  id: string;
  smartuser_id: string;
  achievement_type: string;
  coins_earned: number;
  metadata: any;
  created_at: string;
}

export interface UserGamification {
  smartuser_id: string;
  total_coins: number;
  consecutive_days_streak: number;
  last_visit_date: string;
}

export interface RewardNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  coins: number;
  timestamp: Date;
  isNew: boolean;
}

interface GamificationContextType {
  notifications: RewardNotification[];
  userGamification: UserGamification | null;
  isLoading: boolean;
  isGamificationReady: boolean;
  processEvent: (eventType: string, metadata?: any) => Promise<void>;
  dismissNotification: (notificationId: string) => void;
  clearNotifications: () => void;
  refreshUserGamification: () => Promise<void>;
}

const GamificationContext = createContext<GamificationContextType | null>(null);

export const GamificationProvider = ({ children }: { children: React.ReactNode }) => {
  const { authState } = useAuth();
  const { language } = useTranslation();
  const [notifications, setNotifications] = useState<RewardNotification[]>([]);
  const [userGamification, setUserGamification] = useState<UserGamification | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGamificationReady, setIsGamificationReady] = useState(false);
  const hasProcessedDailyVisit = useRef(false);
  const hasCheckedRecentAchievements = useRef(false);
  const hasLoadedInitialData = useRef(false);
  const notifiedAchievementIds = useRef<Set<string>>(new Set());

  // Protection against concurrent coin deduction calls
  const isDeductingCoinsRef = useRef(false);
  const deductionInProgressForEpisodeRef = useRef<string | null>(null);

  // Reset processing flags when user changes (login/logout)
  useEffect(() => {
    hasProcessedDailyVisit.current = false;
    hasCheckedRecentAchievements.current = false;
    hasLoadedInitialData.current = false;
    notifiedAchievementIds.current = new Set();
    isDeductingCoinsRef.current = false;
    deductionInProgressForEpisodeRef.current = null;
    setIsGamificationReady(false);
    setUserGamification(null);
  }, [authState.user?.smartuserId]);

  // IMMEDIATE LOAD: Load user gamification data as soon as user is authenticated
  // Uses cache-first strategy: load from cache immediately, then refresh in background
  useEffect(() => {
    if (!authState.isLoading && authState.user?.smartuserId && !hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      const smartuserId = authState.user.smartuserId;
      console.log('🎮 [GamificationProvider] IMMEDIATE LOAD: Loading gamification data (cache-first)');

      (async () => {
        try {
          // Step 1: Try to load from cache first (instant)
          const cachedData = userProfileCacheService.getGamification(smartuserId);
          if (cachedData) {
            console.log('🎮 [GamificationProvider] ✅ Loaded from CACHE:', {
              total_coins: cachedData.totalCoins,
              streak: cachedData.consecutiveDaysStreak
            });
            setUserGamification({
              smartuser_id: cachedData.smartuserId,
              total_coins: cachedData.totalCoins,
              consecutive_days_streak: cachedData.consecutiveDaysStreak,
              last_visit_date: cachedData.lastVisitDate || '',
            });
            setIsGamificationReady(true);

            // Step 2: Refresh in background (non-blocking)
            supabaseRequestDeduplicator.fetchGamification(smartuserId, { forceRefresh: true })
              .then((freshData) => {
                if (freshData && freshData.totalCoins !== cachedData.totalCoins) {
                  console.log('🎮 [GamificationProvider] Background refresh found updated data:', {
                    old_coins: cachedData.totalCoins,
                    new_coins: freshData.totalCoins
                  });
                  setUserGamification({
                    smartuser_id: freshData.smartuserId,
                    total_coins: freshData.totalCoins,
                    consecutive_days_streak: freshData.consecutiveDaysStreak,
                    last_visit_date: freshData.lastVisitDate || '',
                  });
                }
              })
              .catch((err) => {
                console.warn('🎮 [GamificationProvider] Background refresh failed:', err);
              });
            return;
          }

          // No cache available, fetch from DB using deduplicator
          console.log('🎮 [GamificationProvider] No cache, fetching from DB...');
          const freshData = await supabaseRequestDeduplicator.fetchGamification(smartuserId);

          if (freshData) {
            setUserGamification({
              smartuser_id: freshData.smartuserId,
              total_coins: freshData.totalCoins,
              consecutive_days_streak: freshData.consecutiveDaysStreak,
              last_visit_date: freshData.lastVisitDate || '',
            });
            console.log('🎮 [GamificationProvider] ✅ Initial gamification data loaded from DB:', {
              total_coins: freshData.totalCoins,
              streak: freshData.consecutiveDaysStreak
            });
          } else {
            console.log('🎮 [GamificationProvider] No gamification record found');
          }
          setIsGamificationReady(true);
        } catch (err) {
          console.error('🎮 [GamificationProvider] Exception loading gamification data:', err);
          setIsGamificationReady(true);
        }
      })();
    } else if (!authState.isLoading && !authState.user) {
      setIsGamificationReady(true);
    }
  }, [authState.user?.smartuserId, authState.isLoading]);

  // Check for recent achievements when user authenticates (signup or login)
  // OPTIMIZATION: Deferred to run after UI is stable using InteractionManager
  useEffect(() => {
    if (!authState.isLoading && authState.user?.smartuserId && !hasCheckedRecentAchievements.current) {
      hasCheckedRecentAchievements.current = true;
      console.log('🎮 GamificationProvider: User authenticated, scheduling achievements check (deferred)');

      // Defer to after UI interactions are complete (Phase 2 equivalent)
      const scheduleAfterUI = () => {
        if (Platform.OS === 'web') {
          if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(() => {
              setTimeout(async () => {
                await new Promise(resolve => setTimeout(resolve, 1000));
                await checkForRecentAchievements();
              }, 100);
            }, { timeout: 2000 });
          } else {
            setTimeout(async () => {
              await new Promise(resolve => setTimeout(resolve, 1000));
              await checkForRecentAchievements();
            }, 100);
          }
        } else {
          InteractionManager.runAfterInteractions(async () => {
            await new Promise(resolve => setTimeout(resolve, 1000));
            await checkForRecentAchievements();
          });
        }
      };

      scheduleAfterUI();
    }
  }, [authState.user?.smartuserId, authState.isLoading]);

  // Process daily visit when user is authenticated
  // OPTIMIZATION: Deferred to run after UI is stable using InteractionManager
  useEffect(() => {
    if (!authState.isLoading && authState.user?.smartuserId && !hasProcessedDailyVisit.current) {
      hasProcessedDailyVisit.current = true;
      console.log('🎮 GamificationProvider: User authenticated, scheduling daily visit (deferred)');

      // Defer to after UI interactions are complete (Phase 2 equivalent)
      if (Platform.OS === 'web') {
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(() => processDailyVisit(), { timeout: 2000 });
        } else {
          setTimeout(() => processDailyVisit(), 0);
        }
      } else {
        InteractionManager.runAfterInteractions(() => {
          processDailyVisit();
        });
      }
    }
  }, [authState.user?.smartuserId, authState.isLoading]);

  const processDailyVisit = async () => {
    console.log('🎮 GamificationProvider: processDailyVisit() called');
    
    if (!authState.user?.smartuserId) return;

    try {
      setIsLoading(true);
      console.log('🎮 Processing daily visit for user:', authState.user.smartuserId);

      // Get or create user gamification record
      let { data: gamificationData, error: gamificationError } = await withSupabaseResult<any[]>(
        supabase
          .from('user_gamification')
          .select('*')
          .eq('smartuser_id', authState.user.smartuserId),
        { operation: 'gamification.dailyVisit.fetchUserGamification', logger: gamLog }
      );

      if (gamificationError) {
        console.error('Error fetching user gamification:', gamificationError);
        return;
      }

      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      let currentGamification = gamificationData && gamificationData.length > 0 ? gamificationData[0] : null;

      // Check if user already visited today
      if (currentGamification?.last_visit_date === today) {
        console.log('🎮 User already visited today, skipping daily visit reward');
        setUserGamification(currentGamification);
        return;
      }

      // Get daily_visit event
      const { data: dailyEvent, error: eventError } = await withSupabaseResult<any[]>(
        supabase
          .from('gamification_events')
          .select(`
            *,
            gamification_event_translations!inner(
              title,
              description,
              message
            )
          `)
          .eq('event_type', 'daily_visit')
          .eq('is_active', true)
          .eq('gamification_event_translations.language_code', language),
        { operation: 'gamification.dailyVisit.fetchEvent', logger: gamLog }
      );

      let dailyEventRecord = dailyEvent && dailyEvent.length > 0 ? dailyEvent[0] : null;
      
      // If no translation found for current language, fallback to English
      if (!dailyEventRecord) {
        const { data: fallbackEvent } = await withSupabaseResult<any[]>(
          supabase
            .from('gamification_events')
            .select(`
              *,
              gamification_event_translations!inner(
                title,
                description,
                message
              )
            `)
            .eq('event_type', 'daily_visit')
            .eq('is_active', true)
            .eq('gamification_event_translations.language_code', 'en'),
          { operation: 'gamification.dailyVisit.fetchEventFallback', logger: gamLog }
        );

        dailyEventRecord = fallbackEvent && fallbackEvent.length > 0 ? fallbackEvent[0] : null;
      }
      
      if (eventError || !dailyEventRecord) {
        console.warn('Daily visit event not found or inactive. Skipping daily visit processing.');
        return;
      }

      // Calculate streak
      let newStreak = 1;
      if (currentGamification) {
        const lastVisit = new Date(currentGamification.last_visit_date);
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        // If last visit was yesterday, increment streak
        if (lastVisit.toISOString().split('T')[0] === yesterday.toISOString().split('T')[0]) {
          newStreak = currentGamification.consecutive_days_streak + 1;
        }
      }

      // Calculate coins earned (streak * base reward)
      const coinsEarned = newStreak * dailyEventRecord.coins_reward;

      // Update or create user gamification record
      const updatedGamification = {
        smartuser_id: authState.user.smartuserId,
        total_coins: (currentGamification?.total_coins || 0) + coinsEarned,
        consecutive_days_streak: newStreak,
        last_visit_date: today,
        updated_at: new Date().toISOString(),
      };

      if (currentGamification) {
        // Update existing record
        const { error: updateError } = await withSupabaseResult(
          supabase
            .from('user_gamification')
            .update(updatedGamification)
            .eq('smartuser_id', authState.user.smartuserId),
          { operation: 'gamification.dailyVisit.updateGamification', logger: gamLog }
        );

        if (updateError) {
          console.error('Error updating user gamification:', updateError);
          return;
        }
      } else {
        // Create new record
        const { error: insertError } = await withSupabaseResult(
          supabase
            .from('user_gamification')
            .insert({
              ...updatedGamification,
              created_at: new Date().toISOString(),
            }),
          { operation: 'gamification.dailyVisit.insertGamification', logger: gamLog }
        );

        if (insertError) {
          console.error('Error creating user gamification:', insertError);
          return;
        }
      }

      // Record achievement with notified_at set since notification will be shown immediately
      const { error: achievementError } = await withSupabaseResult(
        supabase
          .from('user_achievements')
          .insert({
            smartuser_id: authState.user.smartuserId,
            achievement_type: 'daily_visit',
            coins_earned: coinsEarned,
            metadata: {
              streak: newStreak,
              date: today,
            },
            created_at: new Date().toISOString(),
            notified_at: new Date().toISOString(),
          }),
        { operation: 'gamification.dailyVisit.insertAchievement', logger: gamLog }
      );

      if (achievementError) {
        console.error('Error recording achievement:', achievementError);
        console.error('🚨 CRITICAL: Achievement insert failed - rolling back coin update to maintain data consistency');

        // ROLLBACK: Revert the coin update to maintain data consistency
        const rollbackCoins = currentGamification?.total_coins || 0;
        const rollbackStreak = currentGamification?.consecutive_days_streak || 0;
        const rollbackDate = currentGamification?.last_visit_date || null;

        const { error: rollbackError } = await withSupabaseResult(
          supabase
            .from('user_gamification')
            .update({
              total_coins: rollbackCoins,
              consecutive_days_streak: rollbackStreak,
              last_visit_date: rollbackDate,
              updated_at: new Date().toISOString(),
            })
            .eq('smartuser_id', authState.user.smartuserId),
          { operation: 'gamification.dailyVisit.rollbackGamification', logger: gamLog }
        );

        if (rollbackError) {
          console.error('🚨 CRITICAL: Rollback also failed - data may be inconsistent:', rollbackError);
        } else {
          console.log('✅ Rollback successful - coins reverted to:', rollbackCoins);
        }

        // Don't update local state since we rolled back
        return;
      }

      // Update local state only after both operations succeed
      setUserGamification(updatedGamification);

      // Update cache with new gamification data
      userProfileCacheService.setGamification({
        smartuserId: authState.user.smartuserId,
        totalCoins: updatedGamification.total_coins,
        consecutiveDaysStreak: updatedGamification.consecutive_days_streak,
        lastVisitDate: updatedGamification.last_visit_date,
        timestamp: Date.now(),
      });

      // Get translated content
      const translations = (dailyEventRecord as any).gamification_event_translations?.[0];
      const eventTitle = translations?.title || dailyEventRecord.title;
      const eventMessage = translations?.message || `You earned ${coinsEarned} coins for your daily visit`;

      // Show notification
      const notification: RewardNotification = {
        id: `daily_visit_${Date.now()}`,
        type: 'daily_visit',
        title: newStreak === 1 ? eventTitle : `${newStreak} day streak!`,
        message: eventMessage.replace('{coins}', coinsEarned.toString()),
        coins: coinsEarned,
        timestamp: new Date(),
        isNew: true,
      };

      console.log('🔔 GamificationProvider: Creating daily visit notification:', notification);
      setNotifications(prev => {
        const updated = [notification, ...prev];
        console.log('🔔 GamificationProvider: Daily visit notifications state updated, current notifications:', updated.length);
        return updated;
      });

      console.log('🎮 Daily visit processed successfully:', {
        streak: newStreak,
        coinsEarned,
        totalCoins: updatedGamification.total_coins,
      });

      trackEarnCoins(coinsEarned, 'daily_visit', {
        streak: newStreak,
        date: today,
      });

    } catch (error) {
      console.error('Error processing daily visit:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkForRecentAchievements = async () => {
    console.log('🎮 GamificationProvider: checkForRecentAchievements() called');

    if (!authState.user?.smartuserId) {
      console.log('🎮 checkForRecentAchievements: No user authenticated, skipping');
      return;
    }

    try {
      console.log('🎮 checkForRecentAchievements: Checking for recent achievements for user:', authState.user.smartuserId);

      // Query for achievements that have NOT been notified yet (notified_at IS NULL)
      // This persists across app restarts, unlike the previous in-memory approach
      const { data: recentAchievements, error: achievementError } = await withSupabaseResult<any[]>(
        supabase
          .from('user_achievements')
          .select('*')
          .eq('smartuser_id', authState.user.smartuserId)
          .is('notified_at', null)
          .order('created_at', { ascending: true }),
        { operation: 'gamification.checkRecent.fetchAchievements', logger: gamLog }
      );

      if (achievementError) {
        console.error('🎮 checkForRecentAchievements: Error fetching achievements:', achievementError);
        return;
      }

      if (!recentAchievements || recentAchievements.length === 0) {
        console.log('🎮 checkForRecentAchievements: No recent achievements found');
        return;
      }

      console.log('🎮 checkForRecentAchievements: Found', recentAchievements.length, 'recent achievements');

      // Filter out achievements we've already notified about
      const newAchievements = recentAchievements.filter(
        achievement => !notifiedAchievementIds.current.has(achievement.id)
      );

      if (newAchievements.length === 0) {
        console.log('🎮 checkForRecentAchievements: All achievements already notified');
        return;
      }

      console.log('🎮 checkForRecentAchievements: Processing', newAchievements.length, 'new achievements');

      // Process each achievement and create notifications
      for (const achievement of newAchievements) {
        // Skip daily_visit achievements as they're handled by processDailyVisit
        if (achievement.achievement_type === 'daily_visit') {
          console.log('🎮 checkForRecentAchievements: Skipping daily_visit achievement (handled separately)');
          notifiedAchievementIds.current.add(achievement.id);
          continue;
        }

        console.log('🎮 checkForRecentAchievements: Processing achievement:', achievement.achievement_type);

        // Fetch the event configuration for this achievement type
        const { data: eventConfig, error: eventError } = await withSupabaseResult<any[]>(
          supabase
            .from('gamification_events')
            .select(`
              *,
              gamification_event_translations!inner(
                title,
                description,
                message
              )
            `)
            .eq('event_type', achievement.achievement_type)
            .eq('is_active', true)
            .eq('gamification_event_translations.language_code', language),
          { operation: 'gamification.checkRecent.fetchEventConfig', logger: gamLog }
        );

        let eventRecord = eventConfig && eventConfig.length > 0 ? eventConfig[0] : null;

        // If no translation found for current language, fallback to English
        if (!eventRecord) {
          const { data: fallbackEvent } = await withSupabaseResult<any[]>(
            supabase
              .from('gamification_events')
              .select(`
                *,
                gamification_event_translations!inner(
                  title,
                  description,
                  message
                )
              `)
              .eq('event_type', achievement.achievement_type)
              .eq('is_active', true)
              .eq('gamification_event_translations.language_code', 'en'),
            { operation: 'gamification.checkRecent.fetchEventFallback', logger: gamLog }
          );

          eventRecord = fallbackEvent && fallbackEvent.length > 0 ? fallbackEvent[0] : null;
        }

        if (!eventRecord) {
          console.warn('🎮 checkForRecentAchievements: No event config found for', achievement.achievement_type);
          notifiedAchievementIds.current.add(achievement.id);
          continue;
        }

        // Get translated content
        const translations = (eventRecord as any).gamification_event_translations?.[0];
        const eventTitle = translations?.title || eventRecord.title;
        const eventMessage = translations?.message || `You earned ${achievement.coins_earned} coins!`;

        // Create notification
        const notification: RewardNotification = {
          id: `${achievement.achievement_type}_${achievement.id}_${Date.now()}`,
          type: achievement.achievement_type,
          title: eventTitle,
          message: eventMessage.replace('{coins}', achievement.coins_earned.toString()),
          coins: achievement.coins_earned,
          timestamp: new Date(achievement.created_at),
          isNew: true,
        };

        console.log('🔔 checkForRecentAchievements: Creating notification for', achievement.achievement_type);
        setNotifications(prev => {
          const updated = [notification, ...prev];
          console.log('🔔 checkForRecentAchievements: Notifications state updated, current count:', updated.length);
          return updated;
        });

        // Mark as notified in database (persists across app restarts)
        const { error: updateError } = await withSupabaseResult(
          supabase
            .from('user_achievements')
            .update({ notified_at: new Date().toISOString() })
            .eq('id', achievement.id),
          { operation: 'gamification.checkRecent.markNotified', logger: gamLog }
        );

        if (updateError) {
          console.error('🎮 checkForRecentAchievements: Error marking achievement as notified:', updateError);
        } else {
          console.log('🎮 checkForRecentAchievements: Achievement marked as notified in DB:', achievement.id);
        }

        // Also mark in memory for current session (backup)
        notifiedAchievementIds.current.add(achievement.id);
      }

      console.log('🎮 checkForRecentAchievements: Finished processing all recent achievements');

    } catch (error) {
      console.error('🎮 checkForRecentAchievements: Error:', error);
    }
  };

  const processEvent = async (eventType: string, metadata: any = {}) => {
    if (!authState.user?.smartuserId) return;

    try {
      console.log('🎮 GamificationProvider: Processing event:', eventType);
      console.log('🎮 DEBUG: processEvent called with:', {
        eventType,
        metadata,
        smartuserId: authState.user?.smartuserId,
        language
      });

      // Note: user_signup and email_provided events are handled by checkForRecentAchievements()
      // after authentication. This prevents duplicate processing since the achievements are
      // created by the signup edge function before the user is authenticated in the app.

      // Handle batched episode completion notification
      if (eventType === 'episodes_batch_completed') {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('🎮 [EPISODES_BATCH] Processing episodes_batch_completed event');
        console.log('🎮 [EPISODES_BATCH] Platform:', Platform.OS);
        console.log('🎮 [EPISODES_BATCH] Timestamp:', new Date().toISOString());
        const episodeCount = metadata.episodeCount || 1;
        console.log('🎮 [EPISODES_BATCH] Episode count for batch:', episodeCount);
        console.log('🎮 [EPISODES_BATCH] Current notifications count:', notifications.length);
        
        // Get event details for coin calculation and translations
        // Use episodes_batch_completed for multiple episodes, episode_completed for single
        const eventTypeToFetch = episodeCount > 1 ? 'episodes_batch_completed' : 'episode_completed';
        console.log('🎮 [EPISODES_BATCH] Fetching translations for event type:', eventTypeToFetch);

        const { data: episodeEvent, error: episodeEventError } = await withSupabaseResult<any[]>(
          supabase
            .from('gamification_events')
            .select(`
              *,
              gamification_event_translations!inner(
                title,
                description,
                message
              )
            `)
            .eq('event_type', eventTypeToFetch)
            .eq('is_active', true)
            .eq('gamification_event_translations.language_code', language),
          { operation: 'gamification.processEvent.batchFetchEvent', logger: gamLog }
        );

        console.log('Auto close Reward 🎮 DEBUG: Episode event query result:', {
          data: episodeEvent,
          error: episodeEventError,
          language,
          eventTypeToFetch
        });
        let episodeEventRecord = episodeEvent && episodeEvent.length > 0 ? episodeEvent[0] : null;

        // Fallback to English if no translation found
        if (!episodeEventRecord) {
          console.log('Auto close Reward 🎮 DEBUG: No event found for language, trying English fallback...');
          const { data: fallbackEvent } = await withSupabaseResult<any[]>(
            supabase
              .from('gamification_events')
              .select(`
                *,
                gamification_event_translations!inner(
                  title,
                  description,
                  message
                )
              `)
              .eq('event_type', eventTypeToFetch)
              .eq('is_active', true)
              .eq('gamification_event_translations.language_code', 'en'),
            { operation: 'gamification.processEvent.batchFetchEventFallback', logger: gamLog }
          );

          console.log('Auto close Reward 🎮 DEBUG: English fallback query result:', fallbackEvent);
          episodeEventRecord = fallbackEvent && fallbackEvent.length > 0 ? fallbackEvent[0] : null;
        }
        
        if (episodeEventRecord) {
          console.log('Auto close Reward 🎮 DEBUG: Found episode event record, creating notification...');
          const totalCoinsEarned = episodeCount * episodeEventRecord.coins_reward;
          const translations = (episodeEventRecord as any).gamification_event_translations?.[0];

          // For notifications: use the message with {count} and {coins} placeholders
          // The title in DB is generic (for Reward Center), so we use the message for the notification title too
          const eventMessage = (translations?.message || `{count} episodes watched: +{coins} coins`)
            .replace('{count}', episodeCount.toString())
            .replace('{coins}', totalCoinsEarned.toString());

          // Build a dynamic title with the count for the notification
          // This is different from the DB title which is generic for the Reward Center
          const notificationTitle = eventMessage.split(':')[0].trim();

          console.log('🎮 [EPISODES_BATCH] Creating batched notification with:', {
            totalCoinsEarned,
            notificationTitle,
            eventMessage,
            episodeCount
          });
          // Show single notification for all completed episodes
          const notification: RewardNotification = {
            id: `episodes_batch_${Date.now()}`,
            type: 'episode_completed',
            title: notificationTitle,
            message: eventMessage,
            coins: totalCoinsEarned,
            timestamp: new Date(),
            isNew: true,
          };

          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('🔔 [EPISODES_BATCH] Creating notification object:');
          console.log('🔔 [EPISODES_BATCH] - ID:', notification.id);
          console.log('🔔 [EPISODES_BATCH] - Type:', notification.type);
          console.log('🔔 [EPISODES_BATCH] - Title:', notification.title);
          console.log('🔔 [EPISODES_BATCH] - Message:', notification.message);
          console.log('🔔 [EPISODES_BATCH] - Coins:', notification.coins);
          console.log('🔔 [EPISODES_BATCH] - isNew:', notification.isNew);
          console.log('🔔 [EPISODES_BATCH] - Timestamp:', notification.timestamp);
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('🔔 [EPISODES_BATCH] About to update notifications state...');
          setNotifications(prev => {
            const updated = [notification, ...prev];
            console.log('🔔 [EPISODES_BATCH] State updated - Previous count:', prev.length);
            console.log('🔔 [EPISODES_BATCH] State updated - New count:', updated.length);
            console.log('🔔 [EPISODES_BATCH] State updated - All notifications:', updated.map(n => ({
              id: n.id,
              type: n.type,
              isNew: n.isNew,
              title: n.title
            })));
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            return updated;
          });

          trackEarnCoins(totalCoinsEarned, 'episodes_batch_completed', {
            episodeCount,
            episodeIds: metadata.episodeIds || [],
          });

          // Mark all corresponding episode_completed achievements as notified
          // This prevents them from being re-shown on next login
          const episodeIds = metadata.episodeIds || [];
          if (episodeIds.length > 0 && authState.user?.smartuserId) {
            console.log('🎮 [EPISODES_BATCH] Marking episode_completed achievements as notified for episodes:', episodeIds);

            const { error: updateNotifiedError } = await withSupabaseResult(
              supabase
                .from('user_achievements')
                .update({ notified_at: new Date().toISOString() })
                .eq('smartuser_id', authState.user.smartuserId)
                .eq('achievement_type', 'episode_completed')
                .is('notified_at', null)
                .in('metadata->>episodeId', episodeIds.map(id => String(id))),
              { operation: 'gamification.processEvent.markBatchNotified', logger: gamLog }
            );

            if (updateNotifiedError) {
              console.error('🎮 [EPISODES_BATCH] Error marking achievements as notified:', updateNotifiedError);
            } else {
              console.log('🎮 [EPISODES_BATCH] Successfully marked achievements as notified');
            }
          }
        } else {
          console.log('Auto close Reward 🎮 DEBUG: No episode event record found, cannot create notification');
        }

        console.log('Auto close Reward 🎮 DEBUG: episodes_batch_completed processing completed, returning early');
        return; // Exit early for batched notifications
      }

      // Handle coin deduction events
      if (eventType === 'deduct_coins') {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('🪙 [GAMIFICATION] ⭐ COIN DEDUCTION EVENT STARTED ⭐');
        console.log('🪙 [GAMIFICATION] Timestamp:', new Date().toISOString());
        console.log('🪙 [GAMIFICATION] User ID:', authState.user.smartuserId);
        console.log('🪙 [GAMIFICATION] Event metadata:', JSON.stringify(metadata, null, 2));
        console.log('🪙 [GAMIFICATION] Current language:', language);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

        // PROTECTION: Check if episode was already purchased (persisted in local storage)
        if (metadata.episodeId && isEpisodePurchased(authState.user.smartuserId, metadata.episodeId)) {
          console.log('🪙 [GAMIFICATION] ✅ EPISODE ALREADY PURCHASED - skipping deduction');
          console.log('🪙 [GAMIFICATION] Episode ID:', metadata.episodeId);
          console.log('🪙 [GAMIFICATION] User has already paid for this episode, no coins will be deducted');
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          return;
        }

        // PROTECTION: Check if a deduction is already in progress
        if (isDeductingCoinsRef.current) {
          console.log('🪙 [GAMIFICATION] ⚠️  CONCURRENT DEDUCTION PREVENTED - deduction already in progress');
          console.log('🪙 [GAMIFICATION] Current deduction for episode:', deductionInProgressForEpisodeRef.current);
          console.log('🪙 [GAMIFICATION] Requested deduction for episode:', metadata.episodeId);
          return;
        }

        if (!metadata.cost || typeof metadata.cost !== 'number' || metadata.cost <= 0) {
          console.error('🪙 [GAMIFICATION] ❌ VALIDATION FAILED: Invalid cost');
          console.error('🪙 [GAMIFICATION] Cost value:', metadata.cost, 'Type:', typeof metadata.cost);
          // Release lock before returning
          isDeductingCoinsRef.current = false;
          deductionInProgressForEpisodeRef.current = null;
          return;
        }

        // Set deduction in progress flag
        isDeductingCoinsRef.current = true;
        deductionInProgressForEpisodeRef.current = metadata.episodeId || 'unknown';
        console.log('🪙 [GAMIFICATION] 🔒 Deduction lock acquired for episode:', deductionInProgressForEpisodeRef.current);

        // Get current user gamification
        console.log('🪙 [GAMIFICATION] 📊 Step 1: Fetching user gamification data...');
        const { data: gamificationData, error: gamificationError } = await withSupabaseResult<any[]>(
          supabase
            .from('user_gamification')
            .select('*')
            .eq('smartuser_id', authState.user.smartuserId),
          { operation: 'gamification.deductCoins.fetchUserGamification', logger: gamLog }
        );

        console.log('🪙 [GAMIFICATION] Query result:', {
          hasData: !!gamificationData,
          dataLength: gamificationData?.length || 0,
          hasError: !!gamificationError,
          error: gamificationError
        });

        const currentGamification = gamificationData && gamificationData.length > 0 ? gamificationData[0] : null;

        if (!currentGamification) {
          console.error('🪙 [GAMIFICATION] ❌ FAILED: No gamification data found for user');
          console.error('🪙 [GAMIFICATION] User may not have initialized gamification system');
          // Release lock before returning
          isDeductingCoinsRef.current = false;
          deductionInProgressForEpisodeRef.current = null;
          return;
        }

        console.log('🪙 [GAMIFICATION] ✅ User gamification data loaded:', {
          totalCoins: currentGamification.total_coins,
          streak: currentGamification.consecutive_days_streak,
          lastVisit: currentGamification.last_visit_date
        });

        if (currentGamification.total_coins < metadata.cost) {
          console.error('🪙 [GAMIFICATION] ❌ INSUFFICIENT COINS');
          console.error('🪙 [GAMIFICATION] Available:', currentGamification.total_coins);
          console.error('🪙 [GAMIFICATION] Required:', metadata.cost);
          console.error('🪙 [GAMIFICATION] Shortfall:', metadata.cost - currentGamification.total_coins);
          isDeductingCoinsRef.current = false;
          deductionInProgressForEpisodeRef.current = null;
          throw new Error('INSUFFICIENT_COINS');
        }

        console.log('🪙 [GAMIFICATION] ✅ Sufficient coins available');

        // Check if this is the first time EVER that the user is using coins
        console.log('🪙 [GAMIFICATION] 🔍 Step 2: Checking if user has EVER used coins before...');
        console.log('🪙 [GAMIFICATION] Querying user_achievements for ANY coins_used_for_premium event...');

        const { data: hasUsedCoinsBefore, error: achievementQueryError } = await withSupabaseResult<any[]>(
          supabase
            .from('user_achievements')
            .select('id')
            .eq('smartuser_id', authState.user.smartuserId)
            .eq('achievement_type', 'coins_used_for_premium')
            .limit(1),
          { operation: 'gamification.deductCoins.checkFirstTime', logger: gamLog }
        );

        console.log('🪙 [GAMIFICATION] User coin usage history check result:', {
          hasData: !!hasUsedCoinsBefore,
          dataLength: hasUsedCoinsBefore?.length || 0,
          hasError: !!achievementQueryError,
          error: achievementQueryError
        });

        const isFirstTimeEverUsingCoins = !hasUsedCoinsBefore || hasUsedCoinsBefore.length === 0;

        if (isFirstTimeEverUsingCoins) {
          console.log('🪙 [GAMIFICATION] 🎉 FIRST-TIME COIN USAGE DETECTED! 🎉');
          console.log('🪙 [GAMIFICATION] This is the user\'s FIRST TIME EVER using coins');
          console.log('🪙 [GAMIFICATION] Will create achievement record and show notification');
        } else {
          console.log('🪙 [GAMIFICATION] ℹ️  User has used coins before');
          console.log('🪙 [GAMIFICATION] Skipping achievement record (already exists)');
          console.log('🪙 [GAMIFICATION] Will only deduct coins, no notification or achievement');
        }

        // Deduct coins
        console.log('🪙 [GAMIFICATION] 💰 Step 3: Deducting coins from user balance...');
        console.log('🪙 [GAMIFICATION] Before:', currentGamification.total_coins, 'coins');
        console.log('🪙 [GAMIFICATION] Deducting:', metadata.cost, 'coins');
        console.log('🪙 [GAMIFICATION] After:', currentGamification.total_coins - metadata.cost, 'coins');

        const updatedGamification = {
          ...currentGamification,
          total_coins: currentGamification.total_coins - metadata.cost,
          updated_at: new Date().toISOString(),
        };

        console.log('🪙 [GAMIFICATION] Updating user_gamification table...');
        const { error: updateError } = await withSupabaseResult(
          supabase
            .from('user_gamification')
            .update(updatedGamification)
            .eq('smartuser_id', authState.user.smartuserId),
          { operation: 'gamification.deductCoins.updateBalance', logger: gamLog }
        );

        if (updateError) {
          console.error('🪙 [GAMIFICATION] ❌ DATABASE ERROR: Failed to update coins');
          console.error('🪙 [GAMIFICATION] Error details:', updateError);
          // Release lock before returning
          isDeductingCoinsRef.current = false;
          deductionInProgressForEpisodeRef.current = null;
          return;
        }

        console.log('🪙 [GAMIFICATION] ✅ Coins successfully deducted from database');

        // PERSIST: Mark episode as purchased in local storage to prevent future double charges
        if (metadata.episodeId) {
          markEpisodeAsPurchased(authState.user.smartuserId, metadata.episodeId);
          console.log('🪙 [GAMIFICATION] ✅ Episode marked as purchased in local storage:', metadata.episodeId);
        }

        // Record the coin usage in user_achievements ONLY if this is the first time ever
        if (isFirstTimeEverUsingCoins) {
          console.log('🪙 [GAMIFICATION] 📝 Step 4: Recording FIRST-TIME coin usage achievement...');
          const achievementRecord = {
            smartuser_id: authState.user.smartuserId,
            achievement_type: 'coins_used_for_premium',
            coins_earned: 0, // No coins earned, this is informational only
            metadata: {
              firstUseCost: metadata.cost,
              firstUseEpisodeId: metadata.episodeId,
              timestamp: new Date().toISOString(),
            },
          };

          console.log('🪙 [GAMIFICATION] Achievement record:', JSON.stringify(achievementRecord, null, 2));

          const { error: achievementError } = await withSupabaseResult(
            supabase
              .from('user_achievements')
              .insert(achievementRecord),
            { operation: 'gamification.deductCoins.insertFirstTimeAchievement', logger: gamLog }
          );

          if (achievementError) {
            console.error('🪙 [GAMIFICATION] ⚠️  Warning: Failed to record achievement');
            console.error('🪙 [GAMIFICATION] Error details:', achievementError);
            console.error('🪙 [GAMIFICATION] Continuing anyway - coins were deducted');
          } else {
            console.log('🪙 [GAMIFICATION] ✅ First-time coin usage achievement recorded in user_achievements');
          }
        } else {
          console.log('🪙 [GAMIFICATION] ⏭️  Step 4: Skipping achievement record (user has used coins before)');
          console.log('🪙 [GAMIFICATION] Achievement type "coins_used_for_premium" already exists for this user');
        }

        // Update local state
        console.log('🪙 [GAMIFICATION] 🔄 Step 5: Updating local state...');
        setUserGamification(updatedGamification);
        console.log('🪙 [GAMIFICATION] ✅ Local state updated');

        // Update cache with new coin balance
        userProfileCacheService.setGamification({
          smartuserId: authState.user.smartuserId,
          totalCoins: updatedGamification.total_coins,
          consecutiveDaysStreak: updatedGamification.consecutive_days_streak || 0,
          lastVisitDate: updatedGamification.last_visit_date || null,
          timestamp: Date.now(),
        });
        console.log('🪙 [GAMIFICATION] ✅ Cache updated');

        console.log('🪙 [GAMIFICATION] 📊 DEDUCTION SUMMARY:');
        console.log('🪙 [GAMIFICATION]   - Cost:', metadata.cost, 'coins');
        console.log('🪙 [GAMIFICATION]   - Remaining:', updatedGamification.total_coins, 'coins');
        console.log('🪙 [GAMIFICATION]   - First time ever?', isFirstTimeEverUsingCoins);
        console.log('🪙 [GAMIFICATION]   - Language:', language);

        trackSpendCoins(metadata.cost, 'premium_content_unlock', {
          episodeId: metadata.episodeId,
          cost: metadata.cost,
          contentType: 'premium_episode',
        });

        // Process user_signup event for first-time coin usage
        if (isFirstTimeEverUsingCoins) {
          console.log('🪙 [GAMIFICATION] 🎯 Step 6: Triggering user_signup gamification event...');
          console.log('🪙 [GAMIFICATION] This is the first time user is using coins - calling gamify-it edge function');

          try {
            // Call the gamify-it edge function to process the user_signup event
            const gamifyResponse = await fetch(getApiUrl('/gamify-it'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getSupabaseAnonKey()}`,
              },
              body: JSON.stringify({
                event_type: 'user_signup',
                user_id: authState.user.smartuserId,
                metadata: {
                  episodeId: metadata.episodeId,
                  coinsUsed: metadata.cost,
                  timestamp: new Date().toISOString(),
                },
              }),
            });

            const gamifyResult = await gamifyResponse.json();
            console.log('🪙 [GAMIFICATION] ✅ user_signup event processed by edge function:', gamifyResult);

            // Show notification about first coin usage (from edge function response)
            if (gamifyResult.success && !metadata.suppressNotification) {
              console.log('🪙 [GAMIFICATION] 🔔 Showing first-time coin usage notification');

              // Get translation based on current language
              const translations = language === 'fr'
                ? (await import('@/texts/fr')).fr
                : (await import('@/texts/en')).en;

              const notification: RewardNotification = {
                id: `user-signup-coins-${Date.now()}`,
                type: 'user_signup',
                title: '🎉',
                message: translations.firstCoinsUsageNotification,
                coins: 0, // This event gives 0 coins
                timestamp: new Date(),
                isNew: true,
              };

              console.log('🔔 [GAMIFICATION] Creating first-time coin usage notification:', notification);
              setNotifications(prev => {
                const updated = [notification, ...prev];
                console.log('🔔 [GAMIFICATION] Notification added, current count:', updated.length);
                return updated;
              });
            }
          } catch (error) {
            console.error('🪙 [GAMIFICATION] ❌ Error calling gamify-it edge function:', error);
            // Continue anyway - coins were already deducted
          }
        } else {
          console.log('🪙 [GAMIFICATION] ⏭️  Step 6: Skipping user_signup event (not first-time coin usage)');
        }

        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('🪙 [GAMIFICATION] ✅ COIN DEDUCTION EVENT COMPLETED');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

        // Release deduction lock
        isDeductingCoinsRef.current = false;
        deductionInProgressForEpisodeRef.current = null;
        console.log('🪙 [GAMIFICATION] 🔓 Deduction lock released');

        return; // Exit early for coin deduction
      }

      // Get event details
      const { data: event, error: eventError } = await withSupabaseResult<any[]>(
        supabase
          .from('gamification_events')
          .select(`
            *,
            gamification_event_translations!inner(
              title,
              description,
              message
            )
          `)
          .eq('event_type', eventType)
          .eq('is_active', true)
          .eq('gamification_event_translations.language_code', language),
        { operation: 'gamification.processEvent.fetchEvent', logger: gamLog }
      );

      let eventRecord = event && event.length > 0 ? event[0] : null;
      
      // If no translation found for current language, fallback to English
      if (!eventRecord) {
        const { data: fallbackEvent } = await withSupabaseResult<any[]>(
          supabase
            .from('gamification_events')
            .select(`
              *,
              gamification_event_translations!inner(
                title,
                description,
                message
              )
            `)
            .eq('event_type', eventType)
            .eq('is_active', true)
            .eq('gamification_event_translations.language_code', 'en'),
          { operation: 'gamification.processEvent.fetchEventFallback', logger: gamLog }
        );

        eventRecord = fallbackEvent && fallbackEvent.length > 0 ? fallbackEvent[0] : null;
      }
      
      if (eventError || !eventRecord) {
        console.error(`Gamification event '${eventType}' not found for language '${language}' or English fallback`);
        return;
      }

      // Check if user already completed this event (for one-time events, check all time; for daily events, check today)
      const isOneTimeEvent = eventRecord.metadata?.one_time === true;
      const today = new Date().toISOString().split('T')[0];
      
      let achievementQuery = supabase
        .from('user_achievements')
        .select('*')
        .eq('smartuser_id', authState.user.smartuserId)
        .eq('achievement_type', eventType);

     // For episode_completed events, check uniqueness by episode ID
     if (eventType === 'episode_completed' && metadata?.episodeId) {
       achievementQuery = achievementQuery.eq('metadata->>episodeId', metadata.episodeId);
     }
      // For daily events, only check today; for one-time events, check all time
     if (!isOneTimeEvent && eventType !== 'episode_completed') {
        achievementQuery = achievementQuery
          .gte('created_at', `${today}T00:00:00.000Z`)
          .lt('created_at', `${today}T23:59:59.999Z`);
      }

      const { data: existingAchievement } = await withSupabaseResult<any[]>(
        achievementQuery,
        { operation: 'gamification.processEvent.checkExistingAchievement', logger: gamLog }
      );

      if (existingAchievement && existingAchievement.length > 0) {
        console.log(`🎮 Event already completed ${isOneTimeEvent ? 'before' : 'today'}:`, eventType);
        return;
      }

      // Get current user gamification
      const { data: gamificationData } = await withSupabaseResult<any[]>(
        supabase
          .from('user_gamification')
          .select('*')
          .eq('smartuser_id', authState.user.smartuserId),
        { operation: 'gamification.processEvent.fetchUserGamification', logger: gamLog }
      );

      const currentGamification = gamificationData && gamificationData.length > 0 ? gamificationData[0] : null;
      const coinsEarned = eventRecord.coins_reward;

      // Update user coins
      const updatedGamification = {
        smartuser_id: authState.user.smartuserId,
        total_coins: (currentGamification?.total_coins || 0) + coinsEarned,
        consecutive_days_streak: currentGamification?.consecutive_days_streak || 0,
        last_visit_date: currentGamification?.last_visit_date || today,
        updated_at: new Date().toISOString(),
      };

      if (currentGamification) {
        const { error: updateError } = await withSupabaseResult(
          supabase
            .from('user_gamification')
            .update(updatedGamification)
            .eq('smartuser_id', authState.user.smartuserId),
          { operation: 'gamification.processEvent.updateGamification', logger: gamLog }
        );

        if (updateError) {
          console.error('Error updating user gamification:', updateError);
          return;
        }
      } else {
        const { error: insertError } = await withSupabaseResult(
          supabase
            .from('user_gamification')
            .insert({
              ...updatedGamification,
              created_at: new Date().toISOString(),
            }),
          { operation: 'gamification.processEvent.insertGamification', logger: gamLog }
        );

        if (insertError) {
          console.error('Error creating user gamification:', insertError);
          return;
        }
      }

      // Record achievement
      // Set notified_at immediately if notification will be shown (not suppressed)
      const shouldMarkAsNotified = !metadata.suppressNotification;
      const { error: achievementError } = await withSupabaseResult(
        supabase
          .from('user_achievements')
          .insert({
            smartuser_id: authState.user.smartuserId,
            achievement_type: eventType,
            coins_earned: coinsEarned,
            metadata: metadata,
            created_at: new Date().toISOString(),
            ...(shouldMarkAsNotified && { notified_at: new Date().toISOString() }),
          }),
        { operation: 'gamification.processEvent.insertAchievement', logger: gamLog }
      );

      if (achievementError) {
        console.error('Error recording achievement:', achievementError);
        console.error('🚨 CRITICAL: Achievement insert failed for event:', eventType, '- rolling back coin update');

        // ROLLBACK: Revert the coin update to maintain data consistency
        const rollbackCoins = currentGamification?.total_coins || 0;
        const { error: rollbackError } = await withSupabaseResult(
          supabase
            .from('user_gamification')
            .update({
              total_coins: rollbackCoins,
              updated_at: new Date().toISOString(),
            })
            .eq('smartuser_id', authState.user.smartuserId),
          { operation: 'gamification.processEvent.rollbackGamification', logger: gamLog }
        );

        if (rollbackError) {
          console.error('🚨 CRITICAL: Rollback also failed - data may be inconsistent:', rollbackError);
        } else {
          console.log('✅ Rollback successful - coins reverted to:', rollbackCoins);
        }

        return;
      }

      // Update local state only after both operations succeed
      setUserGamification(updatedGamification);

      // Update cache with new gamification data
      userProfileCacheService.setGamification({
        smartuserId: authState.user.smartuserId,
        totalCoins: updatedGamification.total_coins,
        consecutiveDaysStreak: updatedGamification.consecutive_days_streak,
        lastVisitDate: updatedGamification.last_visit_date,
        timestamp: Date.now(),
      });

      // Show notification only if not suppressed (for individual episode events, we suppress to show batched notification)
      if (!metadata.suppressNotification) {
        // Get translated content
        const translations = (eventRecord as any).gamification_event_translations?.[0];
        const eventTitle = translations?.title || eventRecord.title;
        const eventMessage = translations?.message || `You earned ${coinsEarned} coins!`;

        // Show notification
        const notification: RewardNotification = {
          id: `${eventType}_${Date.now()}`,
          type: eventType,
          title: eventTitle,
          message: eventMessage.replace('{coins}', coinsEarned.toString()),
          coins: coinsEarned,
          timestamp: new Date(),
          isNew: true,
        };

        console.log('🔔 GamificationProvider: Creating notification:', notification);
        setNotifications(prev => {
          const updated = [notification, ...prev];
          console.log('🔔 GamificationProvider: Notifications state updated, current notifications:', updated.length);
          return updated;
        });
      }

      trackEarnCoins(coinsEarned, eventType, {
        ...metadata,
        eventTitle: (eventRecord as any).gamification_event_translations?.[0]?.title || eventRecord.title,
      });

      console.log('🎮 Event processed successfully:', {
        eventType,
        coinsEarned,
        totalCoins: updatedGamification.total_coins,
      });

    } catch (error) {
      console.error('Error processing event:', error);
    }
  };

  const dismissNotification = (notificationId: string) => {
    console.log('🔔 GamificationProvider: Dismissing notification:', notificationId);
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, isNew: false }
          : notification
      )
    );
    console.log('🔔 GamificationProvider: Notification dismissed');
  };

  const clearNotifications = () => {
    console.log('🔔 GamificationProvider: Clearing all notifications');
    setNotifications([]);
  };

  const refreshUserGamification = useCallback(async (options: { forceRefresh?: boolean } = {}) => {
    if (!authState.user?.smartuserId) {
      console.log('🎮 refreshUserGamification: No user logged in, skipping');
      return;
    }

    const smartuserId = authState.user.smartuserId;
    console.log('🎮 refreshUserGamification: Fetching data for user:', smartuserId, 'forceRefresh:', options.forceRefresh);

    try {
      // Use deduplicator to avoid duplicate requests and leverage cache
      const freshData = await supabaseRequestDeduplicator.fetchGamification(
        smartuserId,
        { forceRefresh: options.forceRefresh ?? true }
      );

      if (freshData) {
        const freshGamification = {
          smartuser_id: freshData.smartuserId,
          total_coins: freshData.totalCoins,
          consecutive_days_streak: freshData.consecutiveDaysStreak,
          last_visit_date: freshData.lastVisitDate || '',
        };

        console.log('🎮 refreshUserGamification: Fresh data loaded:', {
          total_coins: freshGamification.total_coins,
          consecutive_days_streak: freshGamification.consecutive_days_streak
        });

        setUserGamification(freshGamification);
      } else {
        console.log('🎮 refreshUserGamification: No data found');
        setUserGamification(null);
      }
    } catch (error) {
      console.error('🎮 refreshUserGamification: Unexpected error:', error);
    }
  }, [authState.user?.smartuserId]);

  const contextValue: GamificationContextType = {
    notifications,
    userGamification,
    isLoading,
    isGamificationReady,
    processEvent,
    dismissNotification,
    clearNotifications,
    refreshUserGamification,
  };

  return (
    <GamificationContext.Provider value={contextValue}>
      {children}
    </GamificationContext.Provider>
  );
};

const defaultGamificationContext: GamificationContextType = {
  notifications: [],
  userGamification: null,
  isLoading: false,
  isGamificationReady: false,
  processEvent: async () => {},
  dismissNotification: () => {},
  clearNotifications: () => {},
  refreshUserGamification: async () => {},
};

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    console.warn('[useGamification] Context not available - using default values. This may indicate a rendering order issue.');
    return defaultGamificationContext;
  }
  return context;
};